import type { Maybe } from "@entrilia/shared/types.ts";
import { useCallback } from "react";
import type { FieldConfiguration, Grouping } from "../../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { BiAggregation } from "../../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { DimensionField } from "../../../../../../../../../../api/types/svTypes.ts";
import { isAggregation } from "../../../../../../utilities/fieldsHelper.ts";
import { isNumericField } from "../../../../utils/isNumericField.ts";
import { isReferenceDateField } from "../../../utilities/isReferenceDateField.ts";
import { getDefaultFormatByAggregation } from "../../utils/getNewDefaultFormatFormat.ts";

interface Props {
  field: DimensionField;
  cancel: () => void;
  onRemove: () => void;
  onUpdateConfiguration: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
}

const useDimensionFieldOptions = ({ field, cancel, onRemove, onUpdateConfiguration }: Props) => {
  const isReferencedDateField = isReferenceDateField(field.meta);
  const isNumeric = isNumericField(field);
  const aggregation = isAggregation(field);
  const showGroupings = !aggregation && isReferencedDateField;

  const handleRemove = useCallback(() => {
    onRemove();
    cancel();
  }, [onRemove, cancel]);

  const updateGrouping = useCallback(
    (newGrouping: Grouping | null) => {
      if (field.config.grouping !== newGrouping) {
        onUpdateConfiguration({ grouping: newGrouping }, false);
      }
    },
    [field.config.grouping, onUpdateConfiguration]
  );

  const saveChanges = useCallback(
    (changes: Partial<FieldConfiguration>) => {
      onUpdateConfiguration(changes, false);
    },
    [onUpdateConfiguration]
  );

  const onUpdateAggregation = useCallback(
    (changes: Partial<FieldConfiguration>) => {
      if (changes.aggregation === BiAggregation.None) {
        changes.useCustomConditions = false;
        changes.linkedConditions = undefined;
        changes.customConditions = undefined;
      }
      if (changes.aggregation !== undefined && isAggregationChanged(field, changes.aggregation)) {
        changes.format = getDefaultFormatByAggregation(field, changes.aggregation);
      }

      onUpdateConfiguration(changes, false);
    },
    [onUpdateConfiguration, field]
  );

  return {
    isReferencedDateField,
    isNumeric,
    aggregation,
    showGroupings,
    handleRemove,
    updateGrouping,
    saveChanges,
    onUpdateAggregation,
  };
};

export default useDimensionFieldOptions;

const isAggregationChanged = (currentField: DimensionField, newAggregation: Maybe<BiAggregation>) => {
  return currentField.config.aggregation !== newAggregation;
};
